import { useState, useEffect } from 'react';
import emailjs from '@emailjs/browser';

// Container component (same as in App.js)
const Container = ({ children, className = "" }) => (
  <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${className}`}>
    {children}
  </div>
);

const JobRequestSimplified = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    // Client Information
    clientName: '',
    clientEmail: '',
    clientPhone: '',
    clientCompany: '',
    
    // Case Information
    caseTitle: '',
    caseNumber: '',
    courtLocation: '',
    serviceType: 'personal',
    priority: 'normal',
    serviceDeadline: '',
    
    // Recipient Information
    recipientType: 'individual',
    recipientName: '',
    recipientPhone: '',
    recipientTitle: '',
    recipientRelationship: '',
    
    // Physical Description (RESTORED)
    age: '',
    gender: '',
    height: '',
    weight: '',
    ethnicity: '',
    hair: '',
    eyes: '',
    otherDetails: '',
    
    // Service Address
    street: '',
    city: '',
    state: 'TX',
    zipCode: '',
    
    // Additional Addresses
    additionalAddresses: [],
    
    // Service Instructions
    specialInstructions: '',
    
    // Client Reference Number (RESTORED)
    clientReference: ''
  });

  const [addressAnalysis, setAddressAnalysis] = useState(null);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [jobId, setJobId] = useState(null);

  // Zone data for pricing - ALL STATES with proper NM pricing
  const zoneData = {
    // Texas - Primary Service Area
    'lubbock': { city: 'Lubbock', county: 'Lubbock County', baseFee: 75, mileage: 'Zone 1 - Local', state: 'TX' },
    'levelland': { city: 'Levelland', county: 'Hockley County', baseFee: 100, mileage: 'Zone 2 - 20-40 miles', state: 'TX' },
    'plainview': { city: 'Plainview', county: 'Hale County', baseFee: 125, mileage: 'Zone 3 - 40-60 miles', state: 'TX' },
    'brownfield': { city: 'Brownfield', county: 'Terry County', baseFee: 100, mileage: 'Zone 2 - 20-40 miles', state: 'TX' },
    'littlefield': { city: 'Littlefield', county: 'Lamb County', baseFee: 125, mileage: 'Zone 3 - 40-60 miles', state: 'TX' },
    'lamesa': { city: 'Lamesa', county: 'Dawson County', baseFee: 150, mileage: 'Zone 4 - 60-80 miles', state: 'TX' },
    'childress': { city: 'Childress', county: 'Childress County', baseFee: 175, mileage: 'Zone 5 - 80-100 miles', state: 'TX' },
    'wellington': { city: 'Wellington', county: 'Collingsworth County', baseFee: 175, mileage: 'Zone 5 - 80-100 miles', state: 'TX' },
    'seminole': { city: 'Seminole', county: 'Gaines County', baseFee: 125, mileage: 'Zone 3 - 40-60 miles', state: 'TX' },
    'snyder': { city: 'Snyder', county: 'Scurry County', baseFee: 150, mileage: 'Zone 4 - 60-80 miles', state: 'TX' },
    'tulia': { city: 'Tulia', county: 'Swisher County', baseFee: 125, mileage: 'Zone 3 - 40-60 miles', state: 'TX' },
    'muleshoe': { city: 'Muleshoe', county: 'Bailey County', baseFee: 125, mileage: 'Zone 3 - 40-60 miles', state: 'TX' }
  };

  // New Mexico standard rate
  const newMexicoRate = { baseFee: 125, mileage: 'New Mexico Service Area', state: 'NM' };

  // All US States
  const allStates = {
    'AL': 'Alabama', 'AK': 'Alaska', 'AZ': 'Arizona', 'AR': 'Arkansas', 'CA': 'California',
    'CO': 'Colorado', 'CT': 'Connecticut', 'DE': 'Delaware', 'FL': 'Florida', 'GA': 'Georgia',
    'HI': 'Hawaii', 'ID': 'Idaho', 'IL': 'Illinois', 'IN': 'Indiana', 'IA': 'Iowa',
    'KS': 'Kansas', 'KY': 'Kentucky', 'LA': 'Louisiana', 'ME': 'Maine', 'MD': 'Maryland',
    'MA': 'Massachusetts', 'MI': 'Michigan', 'MN': 'Minnesota', 'MS': 'Mississippi', 'MO': 'Missouri',
    'MT': 'Montana', 'NE': 'Nebraska', 'NV': 'Nevada', 'NH': 'New Hampshire', 'NJ': 'New Jersey',
    'NM': 'New Mexico', 'NY': 'New York', 'NC': 'North Carolina', 'ND': 'North Dakota', 'OH': 'Ohio',
    'OK': 'Oklahoma', 'OR': 'Oregon', 'PA': 'Pennsylvania', 'RI': 'Rhode Island', 'SC': 'South Carolina',
    'SD': 'South Dakota', 'TN': 'Tennessee', 'TX': 'Texas', 'UT': 'Utah', 'VT': 'Vermont',
    'VA': 'Virginia', 'WA': 'Washington', 'WV': 'West Virginia', 'WI': 'Wisconsin', 'WY': 'Wyoming'
  };

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Handle address changes with improved pricing logic
  const handleAddressChange = (e) => {
    handleInputChange(e);
    
    setTimeout(() => {
      const currentCity = e.target.name === 'city' ? e.target.value : formData.city;
      const currentState = e.target.name === 'state' ? e.target.value : formData.state;
      const currentZip = e.target.name === 'zipCode' ? e.target.value : formData.zipCode;
      
      if (currentCity && currentZip && currentZip.length >= 5) {
        const cityLower = currentCity.toLowerCase();
        
        // Check for specific Texas cities first
        const analysis = zoneData[cityLower];
        
        if (analysis) {
          // Texas cities with specific pricing
          let rushFee;
          if (analysis.baseFee <= 75) rushFee = analysis.baseFee + 25;
          else if (analysis.baseFee <= 100) rushFee = analysis.baseFee + 30;
          else if (analysis.baseFee <= 125) rushFee = analysis.baseFee + 35;
          else if (analysis.baseFee <= 150) rushFee = analysis.baseFee + 45;
          else if (analysis.baseFee <= 175) rushFee = analysis.baseFee + 50;
          else rushFee = analysis.baseFee + 55;
          
          setAddressAnalysis({
            ...analysis,
            rushFee: rushFee,
            type: 'texas_zone'
          });
        } else if (currentState === 'NM') {
          // New Mexico - Standard Rate
          const nmRushFee = newMexicoRate.baseFee + 35;
          setAddressAnalysis({
            city: currentCity,
            county: 'New Mexico',
            baseFee: newMexicoRate.baseFee,
            mileage: newMexicoRate.mileage,
            rushFee: nmRushFee,
            type: 'new_mexico'
          });
        } else {
          // All other states - Custom pricing with professional message
          const stateName = allStates[currentState] || currentState;
          setAddressAnalysis({
            city: currentCity,
            county: stateName,
            baseFee: 'Custom Quote',
            mileage: 'Out-of-area service available',
            rushFee: 'Custom Quote',
            type: 'out_of_area',
            message: 'We provide nationwide service through our network. Fees vary by location and complexity.'
          });
        }
      } else {
        setAddressAnalysis(null);
      }
    }, 100);
  };

  // Form validation - DOCUMENTS NOW REQUIRED
  const validateStep = (step) => {
    switch (step) {
      case 1:
        return formData.clientName && formData.clientEmail && formData.clientPhone && formData.clientCompany;
      case 2:
        return formData.caseTitle && formData.caseNumber && formData.courtLocation && formData.recipientName;
      case 3:
        return formData.street && formData.city && formData.zipCode;
      case 4:
        return uploadedFiles.length > 0; // DOCUMENTS NOW REQUIRED
      default:
        return true;
    }
  };

  // Get missing fields
  const getMissingFields = (step) => {
    const missing = [];
    switch (step) {
      case 1:
        if (!formData.clientName) missing.push('Your Name');
        if (!formData.clientEmail) missing.push('Your Email');
        if (!formData.clientPhone) missing.push('Your Phone');
        if (!formData.clientCompany) missing.push('Your Company');
        break;
      case 2:
        if (!formData.caseTitle) missing.push('Case Title');
        if (!formData.caseNumber) missing.push('Case Number');
        if (!formData.courtLocation) missing.push('Court Location');
        if (!formData.recipientName) missing.push('Recipient Name');
        break;
      case 3:
        if (!formData.street) missing.push('Street Address');
        if (!formData.city) missing.push('City');
        if (!formData.zipCode) missing.push('ZIP Code');
        break;
      case 4:
        if (uploadedFiles.length === 0) missing.push('PDF Documents (Required)');
        break;
    }
    return missing;
  };

  // Helper function to convert files to base64 for EmailJS attachments
  const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        // Remove the data URL prefix (data:application/pdf;base64,)
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = error => reject(error);
    });
  };

  // Handle form submission with detailed validation debugging
  const handleSubmit = async () => {
    // Detailed validation debugging
    console.log('=== FORM SUBMISSION DEBUG ===');
    console.log('Current form data:', formData);
    console.log('Uploaded files:', uploadedFiles);
    
    const step1Valid = validateStep(1);
    const step2Valid = validateStep(2);
    const step3Valid = validateStep(3);
    const step4Valid = validateStep(4);
    
    console.log('Step 1 validation:', step1Valid);
    console.log('Step 2 validation:', step2Valid);
    console.log('Step 3 validation:', step3Valid);
    console.log('Step 4 validation:', step4Valid);
    
    // Show what's missing for each step
    console.log('Step 1 missing:', getMissingFields(1));
    console.log('Step 2 missing:', getMissingFields(2));
    console.log('Step 3 missing:', getMissingFields(3));
    console.log('Step 4 missing:', getMissingFields(4));

    // SIMPLIFIED validation - only check truly required fields
    const requiredFieldsCheck = {
      clientName: !!formData.clientName?.trim(),
      clientEmail: !!formData.clientEmail?.trim(),
      clientPhone: !!formData.clientPhone?.trim(),
      clientCompany: !!formData.clientCompany?.trim(),
      caseTitle: !!formData.caseTitle?.trim(),
      caseNumber: !!formData.caseNumber?.trim(),
      courtLocation: !!formData.courtLocation?.trim(),
      recipientName: !!formData.recipientName?.trim(),
      street: !!formData.street?.trim(),
      city: !!formData.city?.trim(),
      zipCode: !!formData.zipCode?.trim(),
      hasFiles: uploadedFiles.length > 0
    };
    
    console.log('Required fields check:', requiredFieldsCheck);
    console.log('Form data values:');
    console.log('- clientName:', `"${formData.clientName}"`);
    console.log('- clientEmail:', `"${formData.clientEmail}"`);
    console.log('- clientPhone:', `"${formData.clientPhone}"`);
    console.log('- clientCompany:', `"${formData.clientCompany}"`);
    console.log('- caseTitle:', `"${formData.caseTitle}"`);
    console.log('- caseNumber:', `"${formData.caseNumber}"`);
    console.log('- courtLocation:', `"${formData.courtLocation}"`);
    console.log('- recipientName:', `"${formData.recipientName}"`);
    console.log('- street:', `"${formData.street}"`);
    console.log('- city:', `"${formData.city}"`);
    console.log('- zipCode:', `"${formData.zipCode}"`);
    console.log('- uploadedFiles count:', uploadedFiles.length);
    
    // Find missing required fields
    const missingRequired = Object.entries(requiredFieldsCheck)
      .filter(([key, value]) => !value)
      .map(([key]) => key);
    
    console.log('Missing required fields:', missingRequired);

    if (missingRequired.length > 0) {
      const errorMessage = `Please complete these required fields: ${missingRequired.join(', ')}`;
      setSubmitError(errorMessage);
      console.log('Submission blocked due to missing fields:', missingRequired);
      return;
    }

    // If we get here, all required fields are present
    console.log('✅ All required fields present, proceeding with submission...');

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      console.log('Submitting job via EmailJS (job details only - customer will email PDFs)...');
      
      // Initialize EmailJS with your public key
      emailjs.init("060cHp_kndKsbaSXs");
      
      // Generate a unique reference for this job
      const jobRef = 'JOB-' + Date.now().toString().slice(-8);
      
      // Simple EmailJS submission (no attachments - free plan)
      const templateParams = {
        to_email: "WesTexLegal@gmail.com",
        from_name: formData.clientName,
        from_email: formData.clientEmail,
        reply_to: formData.clientEmail,
        subject: `Job Request: ${formData.caseTitle} (${formData.caseNumber}) - Ref: ${jobRef}`,
        message: `NEW JOB REQUEST SUBMISSION
Reference: ${jobRef}

═══════════════════════════════════════
CLIENT INFORMATION
═══════════════════════════════════════
Name: ${formData.clientName}
Email: ${formData.clientEmail}
Phone: ${formData.clientPhone}
Company: ${formData.clientCompany}
Reference Number: ${formData.clientReference || 'Not provided'}

═══════════════════════════════════════
CASE DETAILS
═══════════════════════════════════════
Case Title: ${formData.caseTitle}
Case Number: ${formData.caseNumber}
Court Location: ${formData.courtLocation}
Service Priority: ${formData.priority}
Service Deadline: ${formData.serviceDeadline || 'Not specified'}

═══════════════════════════════════════
PERSON TO BE SERVED
═══════════════════════════════════════
Name: ${formData.recipientName}
Phone: ${formData.recipientPhone || 'Not provided'}

PHYSICAL DESCRIPTION:
Age: ${formData.age || 'Not provided'}
Gender: ${formData.gender || 'Not provided'}
Height: ${formData.height || 'Not provided'}
Weight: ${formData.weight || 'Not provided'}
Hair: ${formData.hair || 'Not provided'}
Eyes: ${formData.eyes || 'Not provided'}
Other Details: ${formData.otherDetails || 'Not provided'}

═══════════════════════════════════════
SERVICE ADDRESS
═══════════════════════════════════════
Address: ${formData.street}, ${formData.city}, ${formData.state} ${formData.zipCode}

═══════════════════════════════════════
PDF DOCUMENTS TO FOLLOW
═══════════════════════════════════════
The customer will email you the following PDF documents separately:
${uploadedFiles.map(file => `📎 ${file.name} (${(file.size / 1024 / 1024).toFixed(2)} MB)`).join('\n')}

IMPORTANT: Customer has been instructed to email PDFs to WesTexLegal@gmail.com 
with subject: "PDF Documents - ${jobRef}"

Special Instructions: ${formData.specialInstructions || 'None provided'}

Submitted: ${new Date().toLocaleString()}
WestTex Legal Website Job Request Form

💡 NEXT STEP: Customer will send PDF documents in separate email within 5 minutes.`
      };

      console.log('EmailJS params (job details + PDF instructions):', templateParams);

      // Send via EmailJS (free plan - no attachments)
      const result = await emailjs.send(
        'service_1cw28o6',
        'template_wzizyjs',
        templateParams,
        '060cHp_kndKsbaSXs'
      );

      if (result.status === 200 || result.text === 'OK') {
        // Store job reference and files for success page
        setJobId(jobRef);
        setSubmitSuccess(true);
        setCurrentStep(5);
        
        console.log('✅ Job details submitted successfully via EmailJS!');
        console.log(`📎 Customer will email PDFs separately with reference: ${jobRef}`);
      } else {
        throw new Error(`EmailJS submission failed: ${result.status} ${result.text}`);
      }

      console.log('EmailJS response:', result);

      if (result.status === 200 || result.text === 'OK') {
        // Store PDF info for success page
        setUploadedFiles(uploadedFiles); // Keep for success page display
        setSubmitSuccess(true);
        setCurrentStep(5);
        
        console.log('✅ Job details submitted successfully via EmailJS!');
        console.log('📎 PDF files ready for customer to send separately');
      } else {
        throw new Error(`EmailJS submission failed: ${result.status} ${result.text}`);
      }

      console.log('EmailJS response:', result);

      if (result.status === 200 || result.text === 'OK') {
        // Success - no job ID needed (handled manually)
        setSubmitSuccess(true);
        setCurrentStep(5);
        
        console.log('✅ Job submitted successfully via EmailJS!');
      } else {
        throw new Error(`EmailJS submission failed: ${result.status} ${result.text}`);
      }
    } catch (error) {
      console.error('Job submission error details:', {
        error: error,
        message: error.message,
        response: error.response,
        status: error.response?.status,
        data: error.response?.data
      });
      
      let errorMessage = 'Unable to submit job request. ';
      
      // Handle specific Formspree errors
      if (error.response?.status === 422) {
        errorMessage = '⚠️ Form data validation error. Please check all required fields are completed correctly.';
      } else if (error.response?.status === 429) {
        errorMessage = '⏰ Too many requests. Please wait a moment and try again.';
      } else if (error.response?.status >= 500) {
        errorMessage = '🔧 Server error. Please try again in a few minutes.';
      } else if (error.message && error.message.includes('Network Error')) {
        errorMessage = '🌐 Connection error. Please check your internet connection and try again.';
      } else if (error.code === 'NETWORK_ERROR') {
        errorMessage = '🌐 Network error. Please check your connection and try again.';
      } else {
        // Show the actual error for debugging
        errorMessage += (error.message || 'Unknown error occurred. Please try again.');
      }
      
      setSubmitError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // PDF-only file upload handling with DRAG & DROP
  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    processFiles(files);
  };

  const processFiles = (files) => {
    const validFiles = [];
    
    files.forEach(file => {
      // ONLY ALLOW PDF FILES
      if (file.type !== 'application/pdf') {
        alert(`Only PDF files are allowed. "${file.name}" is not a PDF file.`);
        return;
      }
      
      if (file.size > 10485760) {
        alert(`File ${file.name} is too large. Please keep files under 10MB.`);
        return;
      }
      
      validFiles.push(file);
    });
    
    setUploadedFiles(prev => [...prev, ...validFiles]);
  };

  // Drag and drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    processFiles(files);
  };

  const removeFile = (index) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Navigation
  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 4));
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  if (submitSuccess) {
    return (
      <section className="bg-black min-h-screen">
        <Container className="py-16">
          <div className="max-w-2xl mx-auto text-center">
            <div className="bg-green-500/20 border border-green-500/40 rounded-lg p-8">
              <div className="text-green-400 text-6xl mb-4">✅</div>
              <h2 className="text-3xl font-bold text-green-400 mb-4">Job Request Submitted Successfully!</h2>
              <p className="text-green-200 mb-6">
                Thank you for submitting your process serving request. We have received your information via our secure email system and will contact you shortly to confirm the details, assign a job number, and schedule the service.
              </p>
              <div className="bg-white/10 rounded-lg p-4 mb-6">
                <p className="text-white text-sm">
                  <strong>Job Reference:</strong> {jobId}
                </p>
                <p className="text-white text-sm mt-2">
                  Your job request details have been sent to WesTexLegal@gmail.com
                </p>
              </div>

              {/* PDF Email Instructions */}
              {uploadedFiles.length > 0 && (
                <div className="bg-red-500/20 border border-red-500/40 rounded-lg p-4 mb-6">
                  <h3 className="text-red-400 font-semibold mb-3">📎 IMPORTANT: Email Your PDF Documents</h3>
                  <p className="text-red-200 text-sm mb-3">
                    <strong>FINAL STEP REQUIRED:</strong> You must email your PDF documents to complete this job request.
                  </p>
                  
                  <div className="bg-red-600/20 rounded p-4 mb-4">
                    <h4 className="text-red-300 font-semibold mb-2">Email Instructions:</h4>
                    <div className="text-red-200 text-sm space-y-1">
                      <p><strong>To:</strong> WesTexLegal@gmail.com</p>
                      <p><strong>Subject:</strong> PDF Documents - {jobId}</p>
                      <p><strong>Attach:</strong> The {uploadedFiles.length} PDF file(s) you just uploaded</p>
                    </div>
                  </div>

                  <div className="bg-white/10 rounded p-3 mb-4">
                    <h4 className="text-white font-semibold mb-2">Files to Attach:</h4>
                    <ul className="text-white text-sm list-disc list-inside">
                      {uploadedFiles.map((file, index) => (
                        <li key={index}>{file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)</li>
                      ))}
                    </ul>
                  </div>

                  <div className="text-center">
                    <a 
                      href={`mailto:WesTexLegal@gmail.com?subject=PDF Documents - ${jobId}&body=Hello,%0A%0AAttached are the PDF documents for job request ${jobId}.%0A%0AThank you!`}
                      className="bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 transition-colors inline-block font-semibold"
                    >
                      📧 Open Email to Send PDFs
                    </a>
                  </div>

                  <p className="text-red-300 text-xs mt-3 text-center">
                    ⚠️ Your job request is NOT complete until you email the PDF documents
                  </p>
                </div>
              )}
              <button
                onClick={() => window.location.href = '/'}
                className="bg-red-600 text-white px-8 py-3 rounded-lg hover:bg-red-700 transition-colors"
              >
                Return to Home
              </button>
            </div>
          </div>
        </Container>
      </section>
    );
  }

  return (
    <section className="bg-black min-h-screen">
      <Container className="py-8 md:py-16">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8 md:mb-12">
            <h1 className="text-2xl md:text-4xl font-bold text-white mb-4">Submit Job Request</h1>
            <div className="w-24 h-1 bg-red-500 mx-auto mb-4"></div>
            <p className="text-sm md:text-lg text-white/90">
              Professional process serving for legal documents
            </p>
          </div>

          <div className="bg-white/10 backdrop-blur-sm rounded-lg border border-white/20 p-4 md:p-8">
            {/* Progress Tracker */}
            <div className="mb-6 md:mb-10">
              <div className="flex items-center justify-center max-w-4xl mx-auto">
                {[
                  { number: 1, title: 'Client Information' },
                  { number: 2, title: 'Case Details' },
                  { number: 3, title: 'Service Address' },
                  { number: 4, title: 'Documents' }
                ].map((step, index) => (
                  <div key={step.number} className="flex items-center">
                    <div className={`relative flex items-center justify-center w-8 md:w-12 h-8 md:h-12 rounded-full border-2 transition-all duration-300 ${
                      step.number === currentStep
                        ? 'bg-red-500 border-red-500 text-white shadow-lg scale-110'
                        : step.number < currentStep
                          ? 'bg-green-500 border-green-500 text-white shadow-md'
                          : 'bg-white/10 border-white/30 text-white/60'
                    }`}>
                      {step.number < currentStep ? (
                        <svg className="w-3 md:w-6 h-3 md:h-6" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      ) : (
                        <span className="text-sm md:text-lg font-bold">{step.number}</span>
                      )}
                      
                      {step.number === currentStep && (
                        <div className="absolute inset-0 rounded-full bg-red-500/30 animate-pulse"></div>
                      )}
                    </div>

                    <div className={`ml-2 md:ml-3 transition-all duration-300 hidden md:block ${
                      step.number === currentStep
                        ? 'text-red-400 font-semibold'
                        : step.number < currentStep
                          ? 'text-green-400 font-medium'
                          : 'text-white/60'
                    }`}>
                      <div className="text-xs md:text-sm font-semibold">Step {step.number}</div>
                      <div className="text-xs">{step.title}</div>
                    </div>

                    {index < 3 && (
                      <div className={`flex-1 h-0.5 mx-2 md:mx-6 transition-all duration-500 ${
                        step.number < currentStep
                          ? 'bg-green-500'
                          : 'bg-white/20'
                      }`}>
                        <div className={`h-full transition-all duration-500 ${
                          step.number < currentStep
                            ? 'bg-green-500 w-full'
                            : step.number === currentStep
                              ? 'bg-red-500 w-1/2'
                              : 'w-0'
                        }`}></div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <div className="mt-4 md:mt-8 max-w-2xl mx-auto">
                <div className="flex justify-between text-xs text-white/60 mb-2">
                  <span>Progress</span>
                  <span>{Math.round((currentStep - 1) / 3 * 100)}% Complete</span>
                </div>
                <div className="w-full bg-white/10 rounded-full h-2">
                  <div 
                    className="bg-gradient-to-r from-red-500 to-red-600 h-2 rounded-full transition-all duration-700 ease-out"
                    style={{ width: `${(currentStep - 1) / 3 * 100}%` }}
                  ></div>
                </div>
              </div>

              <div className="text-center mt-4 md:mt-6">
                <div className="text-red-400 font-semibold text-lg">
                  {currentStep === 1 && "Client Information"}
                  {currentStep === 2 && "Case Details"}
                  {currentStep === 3 && "Service Address"}
                  {currentStep === 4 && "Documents & Submission"}
                </div>
                <div className="text-white/70 text-sm mt-1">
                  {currentStep === 1 && "Enter your contact and company information"}
                  {currentStep === 2 && "Provide case details and person to be served"}
                  {currentStep === 3 && "Specify service locations with pricing"}
                  {currentStep === 4 && "Upload documents and submit your request"}
                </div>
              </div>
            </div>

            {/* Step 1: Client Information */}
            {currentStep === 1 && (
              <div className="space-y-4 md:space-y-6">
                <h2 className="text-xl md:text-2xl font-bold text-red-500 mb-4 md:mb-6">Your Information</h2>
                
                <div className="grid md:grid-cols-2 gap-4 md:gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-white mb-2">
                      Your Name *
                    </label>
                    <input
                      type="text"
                      name="clientName"
                      value={formData.clientName}
                      onChange={handleInputChange}
                      className="w-full px-3 md:px-4 py-2 md:py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/60 focus:border-red-500 focus:outline-none"
                      placeholder="John Smith"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-white mb-2">
                      Your Email *
                    </label>
                    <input
                      type="email"
                      name="clientEmail"
                      value={formData.clientEmail}
                      onChange={handleInputChange}
                      className="w-full px-3 md:px-4 py-2 md:py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/60 focus:border-red-500 focus:outline-none"
                      placeholder="john@lawfirm.com"
                      required
                    />
                  </div>
                </div>
                
                <div className="grid md:grid-cols-2 gap-4 md:gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-white mb-2">
                      Your Phone *
                    </label>
                    <input
                      type="tel"
                      name="clientPhone"
                      value={formData.clientPhone}
                      onChange={handleInputChange}
                      className="w-full px-3 md:px-4 py-2 md:py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/60 focus:border-red-500 focus:outline-none"
                      placeholder="(806) 555-0123"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-white mb-2">
                      Your Company *
                    </label>
                    <input
                      type="text"
                      name="clientCompany"
                      value={formData.clientCompany}
                      onChange={handleInputChange}
                      className="w-full px-3 md:px-4 py-2 md:py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/60 focus:border-red-500 focus:outline-none"
                      placeholder="Smith Law Firm"
                      required
                    />
                  </div>
                </div>

                {/* Client Reference Number */}
                <div>
                  <label className="block text-sm font-semibold text-white mb-2">
                    Your Reference Number (Optional)
                  </label>
                  <input
                    type="text"
                    name="clientReference"
                    value={formData.clientReference}
                    onChange={handleInputChange}
                    className="w-full px-3 md:px-4 py-2 md:py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/60 focus:border-red-500 focus:outline-none"
                    placeholder="Internal case reference or file number"
                  />
                </div>
              </div>
            )}

            {/* Step 2: Case Details */}
            {currentStep === 2 && (
              <div className="space-y-4 md:space-y-6">
                <h2 className="text-xl md:text-2xl font-bold text-red-500 mb-4 md:mb-6">Case Information</h2>
                
                <div className="grid md:grid-cols-2 gap-4 md:gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-white mb-2">
                      Case Title *
                    </label>
                    <input
                      type="text"
                      name="caseTitle"
                      value={formData.caseTitle}
                      onChange={handleInputChange}
                      className="w-full px-3 md:px-4 py-2 md:py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/60 focus:border-red-500 focus:outline-none"
                      placeholder="Smith vs. Johnson"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-white mb-2">
                      Case Number *
                    </label>
                    <input
                      type="text"
                      name="caseNumber"
                      value={formData.caseNumber}
                      onChange={handleInputChange}
                      className="w-full px-3 md:px-4 py-2 md:py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/60 focus:border-red-500 focus:outline-none"
                      placeholder="CV-2024-001234"
                      required
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-white mb-2">
                    Court Location *
                  </label>
                  <input
                    type="text"
                    name="courtLocation"
                    value={formData.courtLocation}
                    onChange={handleInputChange}
                    className="w-full px-3 md:px-4 py-2 md:py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/60 focus:border-red-500 focus:outline-none"
                    placeholder="Lubbock County District Court"
                    required
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4 md:gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-white mb-2">
                      Service Priority
                    </label>
                    <select
                      name="priority"
                      value={formData.priority}
                      onChange={handleInputChange}
                      className="w-full px-3 md:px-4 py-2 md:py-3 rounded-lg bg-white/10 border border-white/20 text-white focus:border-red-500 focus:outline-none"
                      style={{ color: 'white', backgroundColor: 'rgba(255, 255, 255, 0.1)' }}
                    >
                      <option value="normal" style={{ color: 'black', backgroundColor: 'white' }}>Normal Service</option>
                      <option value="rush" style={{ color: 'black', backgroundColor: 'white' }}>Rush Service</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-white mb-2">
                      Service Deadline (Optional)
                    </label>
                    <input
                      type="date"
                      name="serviceDeadline"
                      value={formData.serviceDeadline}
                      onChange={handleInputChange}
                      min={new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().split('T')[0]}
                      className="w-full px-3 md:px-4 py-2 md:py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/60 focus:border-red-500 focus:outline-none"
                      style={{ colorScheme: 'dark' }}
                    />
                  </div>
                </div>

                <div className="border-t border-white/20 pt-4 md:pt-6">
                  <h3 className="text-lg font-semibold text-white mb-4">Person to be Served</h3>
                  
                  <div className="grid md:grid-cols-2 gap-4 md:gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-white mb-2">
                        Type
                      </label>
                      <select
                        name="recipientType"
                        value={formData.recipientType}
                        onChange={handleInputChange}
                        className="w-full px-3 md:px-4 py-2 md:py-3 rounded-lg bg-white/10 border border-white/20 text-white focus:border-red-500 focus:outline-none"
                        style={{ color: 'white', backgroundColor: 'rgba(255, 255, 255, 0.1)' }}
                      >
                        <option value="individual" style={{ color: 'black', backgroundColor: 'white' }}>Individual</option>
                        <option value="organization" style={{ color: 'black', backgroundColor: 'white' }}>Organization</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-semibold text-white mb-2">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        name="recipientName"
                        value={formData.recipientName}
                        onChange={handleInputChange}
                        className="w-full px-3 md:px-4 py-2 md:py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/60 focus:border-red-500 focus:outline-none"
                        placeholder="Jane Johnson"
                        required
                      />
                    </div>
                  </div>

                  {/* Physical Description Section - EXPANDED */}
                  <div className="border-t border-white/30 pt-6 mt-6">
                    <h4 className="text-md font-semibold text-white mb-4">Physical Description & Identifiers (Optional)</h4>
                    <p className="text-white/70 text-sm mb-4">
                      These details help our process servers identify the correct person. All information is confidential.
                    </p>
                    
                    <div className="grid md:grid-cols-3 gap-4 md:gap-6">
                      <div>
                        <label className="block text-sm font-semibold text-white mb-2">
                          Phone Number
                        </label>
                        <input
                          type="tel"
                          name="recipientPhone"
                          value={formData.recipientPhone}
                          onChange={handleInputChange}
                          className="w-full px-3 md:px-4 py-2 md:py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/60 focus:border-red-500 focus:outline-none"
                          placeholder="(806) 555-0123"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-semibold text-white mb-2">
                          Approximate Age
                        </label>
                        <input
                          type="text"
                          name="age"
                          value={formData.age}
                          onChange={handleInputChange}
                          className="w-full px-3 md:px-4 py-2 md:py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/60 focus:border-red-500 focus:outline-none"
                          placeholder="35"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-semibold text-white mb-2">
                          Gender
                        </label>
                        <select
                          name="gender"
                          value={formData.gender}
                          onChange={handleInputChange}
                          className="w-full px-3 md:px-4 py-2 md:py-3 rounded-lg bg-white/10 border border-white/20 text-white focus:border-red-500 focus:outline-none"
                          style={{ color: 'white', backgroundColor: 'rgba(255, 255, 255, 0.1)' }}
                        >
                          <option value="" style={{ color: 'black', backgroundColor: 'white' }}>Select Gender</option>
                          <option value="male" style={{ color: 'black', backgroundColor: 'white' }}>Male</option>
                          <option value="female" style={{ color: 'black', backgroundColor: 'white' }}>Female</option>
                          <option value="other" style={{ color: 'black', backgroundColor: 'white' }}>Other</option>
                        </select>
                      </div>
                    </div>
                    
                    <div className="grid md:grid-cols-2 gap-4 md:gap-6 mt-4">
                      <div>
                        <label className="block text-sm font-semibold text-white mb-2">
                          Height
                        </label>
                        <input
                          type="text"
                          name="height"
                          value={formData.height}
                          onChange={handleInputChange}
                          className="w-full px-3 md:px-4 py-2 md:py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/60 focus:border-red-500 focus:outline-none"
                          placeholder="5 feet 8 inches"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-semibold text-white mb-2">
                          Weight
                        </label>
                        <input
                          type="text"
                          name="weight"
                          value={formData.weight}
                          onChange={handleInputChange}
                          className="w-full px-3 md:px-4 py-2 md:py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/60 focus:border-red-500 focus:outline-none"
                          placeholder="180 lbs"
                        />
                      </div>
                    </div>
                    
                    <div className="grid md:grid-cols-3 gap-4 md:gap-6 mt-4">
                      <div>
                        <label className="block text-sm font-semibold text-white mb-2">
                          Ethnicity
                        </label>
                        <input
                          type="text"
                          name="ethnicity"
                          value={formData.ethnicity}
                          onChange={handleInputChange}
                          className="w-full px-3 md:px-4 py-2 md:py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/60 focus:border-red-500 focus:outline-none"
                          placeholder="Hispanic, Caucasian, etc."
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-semibold text-white mb-2">
                          Hair Color/Style
                        </label>
                        <input
                          type="text"
                          name="hair"
                          value={formData.hair}
                          onChange={handleInputChange}
                          className="w-full px-3 md:px-4 py-2 md:py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/60 focus:border-red-500 focus:outline-none"
                          placeholder="Brown, Bald, Long Blonde"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-semibold text-white mb-2">
                          Eye Color
                        </label>
                        <input
                          type="text"
                          name="eyes"
                          value={formData.eyes}
                          onChange={handleInputChange}
                          className="w-full px-3 md:px-4 py-2 md:py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/60 focus:border-red-500 focus:outline-none"
                          placeholder="Blue, Brown, Green, Hazel"
                        />
                      </div>
                    </div>
                    
                    <div className="mt-4">
                      <label className="block text-sm font-semibold text-white mb-2">
                        Additional Identifying Details
                      </label>
                      <textarea
                        name="otherDetails"
                        value={formData.otherDetails}
                        onChange={handleInputChange}
                        rows="3"
                        className="w-full px-3 md:px-4 py-2 md:py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/60 focus:border-red-500 focus:outline-none"
                        placeholder="Distinguishing marks, typical clothing, vehicle description, work schedule, or other helpful details..."
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Service Address */}
            {currentStep === 3 && (
              <div className="space-y-4 md:space-y-6">
                <h2 className="text-xl md:text-2xl font-bold text-red-500 mb-4 md:mb-6">Service Address</h2>
                
                <div>
                  <label className="block text-sm font-semibold text-white mb-2">
                    Street Address *
                  </label>
                  <input
                    type="text"
                    name="street"
                    value={formData.street}
                    onChange={handleAddressChange}
                    className="w-full px-3 md:px-4 py-2 md:py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/60 focus:border-red-500 focus:outline-none"
                    placeholder="123 Main Street"
                    required
                  />
                </div>
                
                <div className="grid md:grid-cols-3 gap-4 md:gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-white mb-2">
                      City *
                    </label>
                    <input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleAddressChange}
                      className="w-full px-3 md:px-4 py-2 md:py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/60 focus:border-red-500 focus:outline-none"
                      placeholder="Lubbock"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-white mb-2">
                      State *
                    </label>
                    <select
                      name="state"
                      value={formData.state}
                      onChange={handleAddressChange}
                      className="w-full px-3 md:px-4 py-2 md:py-3 rounded-lg bg-white/10 border border-white/20 text-white focus:border-red-500 focus:outline-none"
                      style={{ color: 'white', backgroundColor: 'rgba(255, 255, 255, 0.1)' }}
                      required
                    >
                      {Object.entries(allStates).map(([code, name]) => (
                        <option key={code} value={code} style={{ color: 'black', backgroundColor: 'white' }}>
                          {code} - {name}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-white mb-2">
                      ZIP Code *
                    </label>
                    <input
                      type="text"
                      name="zipCode"
                      value={formData.zipCode}
                      onChange={handleAddressChange}
                      className="w-full px-3 md:px-4 py-2 md:py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/60 focus:border-red-500 focus:outline-none"
                      placeholder="79401"
                      maxLength="5"
                      required
                    />
                  </div>
                </div>

                {/* Improved Pricing Analysis */}
                {addressAnalysis && (
                  <div className={`mt-6 rounded-lg p-4 ${
                    addressAnalysis.type === 'texas_zone' ? 'bg-green-500/20 border-green-500/40' :
                    addressAnalysis.type === 'new_mexico' ? 'bg-blue-500/20 border-blue-500/40' :
                    'bg-yellow-500/20 border-yellow-500/40'
                  }`}>
                    <h3 className={`text-lg font-semibold mb-3 ${
                      addressAnalysis.type === 'texas_zone' ? 'text-green-400' :
                      addressAnalysis.type === 'new_mexico' ? 'text-blue-400' :
                      'text-yellow-400'
                    }`}>
                      {addressAnalysis.type === 'texas_zone' && '🎯 Texas Service Zone'}
                      {addressAnalysis.type === 'new_mexico' && '🏔️ New Mexico Service'}
                      {addressAnalysis.type === 'out_of_area' && '🌎 Nationwide Network Service'}
                    </h3>
                    
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-white/90">
                          <span className="font-semibold">Location:</span> {addressAnalysis.city}, {addressAnalysis.county}
                        </p>
                        <p className="text-white/90">
                          <span className="font-semibold">Service Area:</span> {addressAnalysis.mileage}
                        </p>
                      </div>
                      <div>
                        <p className="text-white/90">
                          <span className="font-semibold">Standard Fee:</span> {
                            addressAnalysis.baseFee === 'Custom Quote' 
                              ? addressAnalysis.baseFee 
                              : `$${addressAnalysis.baseFee}`
                          }
                        </p>
                        <p className="text-white/90">
                          <span className="font-semibold">Rush Fee:</span> {
                            addressAnalysis.rushFee === 'Custom Quote' 
                              ? addressAnalysis.rushFee 
                              : `$${addressAnalysis.rushFee}`
                          }
                        </p>
                      </div>
                    </div>
                    
                    {addressAnalysis.message && (
                      <div className="mt-3 p-3 bg-white/10 rounded border border-white/20">
                        <p className="text-yellow-200 text-sm">
                          💡 <strong>Nationwide Service:</strong> {addressAnalysis.message}
                        </p>
                      </div>
                    )}
                    
                    <p className={`text-sm mt-2 ${
                      addressAnalysis.type === 'texas_zone' ? 'text-green-300' :
                      addressAnalysis.type === 'new_mexico' ? 'text-blue-300' :
                      'text-yellow-300'
                    }`}>
                      {addressAnalysis.type === 'texas_zone' && '* Primary service area with standard rates'}
                      {addressAnalysis.type === 'new_mexico' && '* New Mexico standard service rate'}
                      {addressAnalysis.type === 'out_of_area' && '* Custom quote provided within 24 hours of submission'}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Step 4: PDF Documents REQUIRED */}
            {currentStep === 4 && (
              <div className="space-y-4 md:space-y-6">
                <h2 className="text-xl md:text-2xl font-bold text-red-500 mb-4 md:mb-6">Upload PDF Documents *</h2>
                <p className="text-yellow-400 text-sm font-medium mb-4">
                  ⚠️ PDF documents are required to submit your job request
                </p>
                
                <div 
                  className={`border-2 border-dashed rounded-lg p-4 md:p-8 text-center transition-colors ${
                    isDragging 
                      ? 'border-red-500 bg-red-500/10' 
                      : 'border-white/30 hover:border-red-500 hover:bg-white/5'
                  }`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <div className="flex flex-col items-center">
                    <div className="text-4xl mb-4">
                      {isDragging ? '📥' : '📄'}
                    </div>
                    <h3 className="text-lg font-semibold text-white mb-2">
                      {isDragging ? 'Drop PDF Files Here' : 'Upload PDF Documents'}
                    </h3>
                    <p className="text-white/70 mb-4 text-sm md:text-base">
                      {isDragging 
                        ? 'Release to upload PDF files' 
                        : 'Drag and drop PDF files here, or click to browse'
                      }
                    </p>
                    <p className="text-white/50 text-xs mb-4">
                      Only PDF files accepted • Max 10MB per file • Required for submission
                    </p>
                    <input
                      type="file"
                      multiple
                      accept=".pdf"
                      onChange={handleFileUpload}
                      className="hidden"
                      id="fileInput"
                      required
                    />
                    <label
                      htmlFor="fileInput"
                      className="bg-red-600 text-white px-4 md:px-6 py-2 rounded-lg hover:bg-red-700 transition-colors cursor-pointer inline-block"
                    >
                      Choose PDF Files
                    </label>
                  </div>
                </div>

                {uploadedFiles.length > 0 && (
                  <div>
                    <h4 className="text-green-400 font-semibold mb-3">
                      ✅ Uploaded PDF Files ({uploadedFiles.length}):
                    </h4>
                    <div className="space-y-2">
                      {uploadedFiles.map((file, index) => (
                        <div key={index} className="flex items-center justify-between bg-green-500/10 rounded-lg p-3 border border-green-500/40">
                          <div className="flex items-center">
                            <span className="text-2xl mr-3">📄</span>
                            <div>
                              <span className="text-white text-sm font-medium">{file.name}</span>
                              <div className="text-green-300 text-xs">
                                {(file.size / 1024 / 1024).toFixed(2)} MB • PDF Document
                              </div>
                            </div>
                          </div>
                          <button
                            onClick={() => removeFile(index)}
                            className="text-red-400 hover:text-red-300 px-3 py-1 rounded hover:bg-red-500/20 transition-colors"
                          >
                            ✕ Remove
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {uploadedFiles.length === 0 && (
                  <div className="bg-yellow-500/20 border border-yellow-500/40 rounded-lg p-4">
                    <p className="text-yellow-300 text-sm">
                      📋 <strong>Required:</strong> You must upload at least one PDF document to proceed with your job request.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Navigation */}
            <div className="flex justify-between mt-6 md:mt-8 pt-6 border-t border-white/20">
              <button
                onClick={prevStep}
                disabled={currentStep === 1}
                className={`px-4 md:px-6 py-2 md:py-3 rounded-lg font-semibold transition-colors ${
                  currentStep === 1
                    ? 'bg-white/10 text-white/50 cursor-not-allowed'
                    : 'bg-white/20 text-white hover:bg-white/30'
                }`}
              >
                Previous
              </button>
              
              {currentStep < 4 ? (
                <button
                  onClick={nextStep}
                  disabled={!validateStep(currentStep)}
                  className={`px-4 md:px-6 py-2 md:py-3 rounded-lg font-semibold transition-colors ${
                    !validateStep(currentStep)
                      ? 'bg-white/10 text-white/50 cursor-not-allowed'
                      : 'bg-red-600 text-white hover:bg-red-700'
                  }`}
                >
                  Next
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  disabled={isSubmitting || !validateStep(1) || !validateStep(2) || !validateStep(3) || !validateStep(4)}
                  className={`px-4 md:px-8 py-2 md:py-3 rounded-lg font-semibold transition-colors ${
                    isSubmitting || !validateStep(1) || !validateStep(2) || !validateStep(3) || !validateStep(4)
                      ? 'bg-white/10 text-white/50 cursor-not-allowed'
                      : 'bg-red-600 text-white hover:bg-red-700'
                  }`}
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Job Request'}
                </button>
              )}
              
              {/* Submit Button Status Message */}
              {currentStep === 4 && (
                <div className="mt-4 text-center">
                  {!validateStep(1) || !validateStep(2) || !validateStep(3) ? (
                    <p className="text-yellow-400 text-sm">
                      ⚠️ Please complete all required fields in previous steps
                    </p>
                  ) : !validateStep(4) ? (
                    <p className="text-yellow-400 text-sm">
                      📄 Please upload at least one PDF document to submit your job request
                    </p>
                  ) : (
                    <p className="text-green-400 text-sm">
                      ✅ All requirements met - ready to submit!
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Error Display */}
            {submitError && (
              <div className="mt-6 bg-red-500/20 border border-red-500/40 rounded-lg p-4">
                <div className="flex items-center">
                  <div>
                    <p className="text-red-400 font-semibold">Submission Error</p>
                    <p className="text-red-300 text-sm">{submitError}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </Container>
    </section>
  );
};

export default JobRequestSimplified;